import type { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';

/**
 * The single statement that there is no connection (021 FR-006, FR-007, FR-022).
 *
 * ONE component, deliberately NOT a ninth copy of `shared/problem/problem-banner`. 014
 * recorded eight hand-copied banner sites awaiting a cross-cutting pass, and FR-007 requires
 * exactly one statement of the offline condition; a per-screen variant would make both worse.
 *
 * It is mounted in TWO PLACES, and neither is redundant — do not delete one on sight.
 * `layout.tsx` shows it inside `main` so the chrome stays usable (FR-022); `app-root.tsx` shows it
 * on every surface OUTSIDE the shell, which includes `/sign-in` after an offline launch and the
 * link-token routes `/password-reset` and `/verify-email` that a SIGNED-IN practitioner can reach
 * from a mailbox. Deleting the root mount restores exactly the no-indication bug
 * `core/pwa/offline-presentation.ts` records. Only one is ever on screen: the shell CLAIMS the
 * panel while mounted and the root renders only when unclaimed.
 *
 * It spends no colour (FR-033). Being offline is not a time pressure, and the four urgency bands
 * are the only hues this application paints.
 *
 * `role="status"` with `aria-live="polite"`, not `alert`/`assertive` (FR-020): the panel appears
 * without the practitioner doing anything, so it must be announced — but assertively would cut
 * across whatever a screen reader is reading, and moving focus here would take the caret out of a
 * half-typed action, which is the harm FR-012 exists to prevent. Announced at the next natural
 * pause; takes nothing.
 */
export function OfflinePanel({ offerGuide = false }: { readonly offerGuide?: boolean }): ReactElement {
  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="offline-panel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-8) var(--space-4)',
        color: 'var(--muted)',
        '& .material-icons': { width: 40, height: 40, fontSize: 40 },
        '& h2': {
          margin: 0,
          fontFamily: 'var(--font-label)',
          fontSize: 'var(--text-lg)',
          color: 'var(--ink)',
        },
        // A measure, not the full width: this sentence is the whole content of the screen while it
        // shows.
        '& p': { margin: 0, maxWidth: '32rem', fontSize: 'var(--text-base)' },
        // Explicit, like every anchor this cycle adds: a bare link takes the user agent's blue, a
        // fifth hue that neither source-reading palette guard can see (013).
        '& a': { color: 'var(--ink)', textDecoration: 'underline', textUnderlineOffset: '2px' },
        '& a:focus-visible': { outline: '2px solid var(--ink)', outlineOffset: '2px' },
        // FR-033: a panel that fades in is a panel that moves, for someone who asked things not to.
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' },
      }}
    >
      <span className="material-icons" aria-hidden="true">cloud_off</span>
      <h2>You are offline</h2>
      <p>
        CompleteMe needs a connection to show your actions. Nothing you have typed is lost — this
        page will load as soon as you are back.
      </p>
      {/*
        033: offered only from the SHELL mount. The root mount covers /sign-in and the link-token
        routes, where /help sits behind the signed-in guard and the link would bounce the reader to
        a sign-in page they cannot complete while offline — a dead end offered as a consolation.
      */}
      {offerGuide && (
        <p>
          <RouterLink to="/help" data-testid="offline-guide-link">
            The user guide still works offline.
          </RouterLink>
        </p>
      )}
    </Box>
  );
}
